import {texture, spine, MiniSignal} from "p3";
import common from "../common";
import definitions from "../definitions";
import GameScene from "./GameScene";
import MenuScene from "./MenuScene";
import LanguagePanel from "../ui/language/LanguagePanel";
import screenfull from 'screenfull';
import { getRandomFloat, locale, resizeLabelToFitText, underlineMultilineText } from "../utils";
import settings from "../settings";
import { Skin } from "@pixi-spine/runtime-4.1";
import { isMobile } from "pixi.js-legacy";

export default class InitialAppScene extends MenuScene {
    constructor() {
        super("initialapp");
    }

    init() {
        super.init();

        this._termsHolder = null;
    }

    destroy() {
        this.removeAllTweens(true);
        super.destroy();
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        console.log('onSceneLoaded');
        const bg = new PIXI.Sprite(texture("splash_bg_1x"));
        bg.width = common.STAGE_WIDTH;
        this.addChild(bg);

        const fill = new PIXI.Sprite(PIXI.Texture.WHITE);
        fill.tint = 0x000000;
        fill.alpha = 0.7;
        fill.interactive = true;
        fill.width = common.STAGE_WIDTH;
        fill.height = common.STAGE_HEIGHT;
        this.addChild(fill);

        this._termsHolder = new PIXI.Container();
        this.addChild(this._termsHolder);

        this._languagePanel = new LanguagePanel(true);
        this._languagePanel.signals.languageChange.add(this._onLanguageChange, this);
        this._languagePanel.position.set(
            (common.STAGE_WIDTH/2) - (this._languagePanel.width/2),
            common.STAGE_HEIGHT,
        );
        this.addChild(this._languagePanel);

        this._tweens.push(
            gsap.to(this._languagePanel, 0.2, {delay:1, y:(common.STAGE_HEIGHT / 2) - (this._languagePanel.height/2), ease:Expo.easeOut})
        );
    }

    _hideLanguagePanel() {

        this._tweens.push(
            gsap.to(this._languagePanel, 0.2, {y:common.STAGE_HEIGHT, ease:Expo.easeIn, onComplete:() => {
                this.removeChild(this._languagePanel);
                this._languagePanel = null;
            }})
        );
    }

    _onLanguageChange(button) {
        this._languagePanel.showConfirm(this, this._tweens, () => {
            common.config.language = button.id;
            common.tracking.setLanguageState(definitions.TRACKING.LANGUAGE_STATE.ENTRY);
            common.saveData.settings.language = button.id;
            common.saveData.settings.languageState = definitions.TRACKING.LANGUAGE_STATE.ENTRY;
            common.saveData.saveGame();
            this._hideLanguagePanel();
            this._showTerms();
            this._createAgreementButton();
        });
    }

    _showTerms() {

		// styles 
		const headerStyle = new PIXI.TextStyle({
            fill: 0xffffff,
            fontFamily: "FredFredBurger",
            fontSize: 50
        });

		const bodyStyle = new PIXI.TextStyle({
            fontFamily: "FredFredBurger",
            fontSize: 28,
            align: common.config.language === 'ar' ? 'right' : 'center',
            fill: 0xffffff,
            wordWrap: true,
            wordWrapWidth: 750
        });


        const headerLabel = new PIXI.Text(locale('legal_header'), headerStyle);
        headerLabel.anchor.set(0.5, 0);
        headerLabel.position = new PIXI.Point(common.STAGE_WIDTH * 0.5, 20);
        this._termsHolder.addChild(headerLabel);


		const body = new PIXI.Container();

		// section 1 
        const body1Label = new PIXI.Text(locale('legal_section1'), bodyStyle);
		body1Label.anchor.set(0.5, 0);
        body1Label.position = new PIXI.Point(common.STAGE_WIDTH * 0.5, 0);
        body.addChild(body1Label);

		const body1LinkLabel = new PIXI.Text(locale('legal_section1_link'), bodyStyle);
		body1LinkLabel.anchor.set(0.5, 0);
		body1LinkLabel.position = new PIXI.Point(common.STAGE_WIDTH * 0.5, body1Label.y + body1Label.height + 10);
		body.addChild(body1LinkLabel);
        
        //section 2
        const body2Label = new PIXI.Text(locale('legal_section2'), bodyStyle);
		body2Label.anchor.set(0.5, 0);
        body2Label.position = new PIXI.Point(common.STAGE_WIDTH * 0.5, body1LinkLabel.y + body1LinkLabel.height + 40);
        body.addChild(body2Label);
		
		// section 3 
		const body3LinkLabel = new PIXI.Text(locale('legal_section3_link'), bodyStyle);
		body3LinkLabel.anchor.set(0.5, 0);
		body3LinkLabel.position = new PIXI.Point(common.STAGE_WIDTH * 0.5, body2Label.y + body2Label.height + 40);
		body.addChild(body3LinkLabel);

		this._termsHolder.addChild(body);
		body.position.set(0, headerLabel.y + headerLabel.height + 20);//P3.View.height * 0.5 - body.height * 0.5);

		// proper positioning for RTL
		if (common.config.language === 'ar') {
			body1Label.anchor.x = 1;
			body1Label.position.x = common.STAGE_WIDTH * 0.5 + body1Label.width * 0.5;

			body1LinkLabel.anchor.x = 1;
			body1LinkLabel.position.x = body1Label.position.x;

			body3LinkLabel.anchor.x = 1;
			body3LinkLabel.position.x = body1Label.position.x;
		}

		underlineMultilineText(body1LinkLabel, bodyStyle);
		this.interactiveText(body1LinkLabel, locale('legal_terms_link'));

		underlineMultilineText(body3LinkLabel, bodyStyle);
		this.interactiveText(body3LinkLabel, locale('legal_privacy_policy_link'));
	}

    _createAgreementButton() {
		const container = new PIXI.Container();
		const button = new PIXI.Graphics();
		button.lineStyle(3, 0x00b0f1, 1);
		button.beginFill(0x0, 1);
		button.drawRoundedRect(0, 0, 50, 50, 4);
		button.endFill();
		button.interactive = true;
		button.cursor = 'pointer';
		button.on('pointertap', () => this.onAgreementAccept());
        this._termsButton = button;
		container.addChild(button);

        const tick = new PIXI.Sprite(texture("btn_icon_medium_tick"));
        tick.anchor.set(0.5);
        tick.scale.set(0);
        button.tick = tick;
        tick.x = button.x + (button.width/2);
        tick.y = button.y + (button.height/2);
        container.addChild(tick);

		const style = new PIXI.TextStyle({
            fontFamily: "FredFredBurger",
            fontSize: 27,
            align: common.config.language === 'ar' ? 'right' : 'left',
            fill: 0xffffff,
            wordWrap: true,
            wordWrapWidth: 500
		});

		const agreementLabel = new PIXI.Text(locale("legal_confirmation"), style);
		agreementLabel.anchor.set(1, 0.5);
		agreementLabel.position = new PIXI.Point(button.width + 30 + agreementLabel.width, button.height * 0.5);
		container.addChild(agreementLabel);
		
		underlineMultilineText(agreementLabel, style, 'all');
		this.interactiveText(agreementLabel, locale('legal_privacy_policy_link'));
		
		this._termsHolder.addChild(container);
		container.position.set(common.STAGE_WIDTH * 0.5 - container.width * 0.5, common.STAGE_HEIGHT - 120);
	}

    underlineText(text) {
		const g = new PIXI.Graphics();
		g.beginFill(0xffffff);
		g.drawRect(text.width * -text.anchor.x, text.height, text.width, 1);
		g.endFill();
		text.addChild(g);
	}

	interactiveText(text, url) {
		text.interactive = true;
		text.cursor = 'pointer';

		text.on('pointertap', () => {
			if (!!window.cordova) {
                if (isMobile.apple.any) {
				    cordova.InAppBrowser.open(url, '_blank', 'location=no');
                } else {
                    window.open(url, '_blank', 'location=no');
                }
			} else {
				window.open(url, '_system');
			}
		});
	}

    onAgreementAccept() {
        this._tweens.push(
            gsap.to(this._termsButton.tick.scale, 1, {x:1, y:1, ease:Elastic.easeOut, onComplete:() => {
                this.signals.next.dispatch();
            }})
        );
	}

    update() {
        super.update();

    }

    resize() {
        super.resize();

    }

}